import { getEnv } from '@/composables/useEnv'
import { useHandleError } from '@/composables/useHandleError'
import { isTransportError } from '@/services/dataStore/isTransportError'
import {
  SanityDataStore,
  TPerspective
} from '@/services/dataStore/SanityDataStore'
import { StaticDataStore } from '@/services/dataStore/StaticDataStore'
import { computed, ComputedRef, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { ZodError } from 'zod'

const rethrownFromCaller = (e: unknown) => {
  const original = e instanceof Error ? e : new Error(String(e))
  const reported = new Error(original.message, { cause: original })
  reported.name = original.name
  return reported
}

export default () => {
  // 'static' or 'sanity'
  const dataStoreSource = getEnv('VITE_APP_DATA_SOURCE') ?? 'static'
  const environment = getEnv('VITE_APP_ENV')
  const route = useRoute()
  const { logError } = useHandleError()

  const perspective: ComputedRef<TPerspective> = computed(() => {
    const queryPerspective = route.query?.perspective
    if (queryPerspective === 'drafts') return 'previewDrafts'
    return 'published' // default to published
  })

  // Invalidate SWRV caches if perspective changes
  interface DataStoreSWRVKey {
    perspective: TPerspective
  }
  const dataStoreSWRVKey = ref({
    perspective: perspective.value
  } as DataStoreSWRVKey)

  watch(perspective, () => {
    dataStoreSWRVKey.value.perspective = perspective.value
  })

  // In lower environments, allow invalid draft content through to allow testing new content.
  //   Be warned - invalid draft content can break things in the UI!
  // To override this for debugging validations, updating here will flip the behavior everywhere.
  const disableValidations = computed(
    () => environment !== 'production' && perspective.value === 'previewDrafts'
  )

  const dataStore = computed(() => {
    switch (dataStoreSource) {
      case 'sanity': {
        try {
          return new SanityDataStore(
            perspective.value,
            disableValidations.value
          )
        } catch (e) {
          console.warn('Error initializing SanityDataStore', e)
        }
        // falls through if error
      }
      case 'static':
      default: {
        return new StaticDataStore()
      }
    }
  })

  const handleDataStoreError = (e: unknown, source: string) => {
    const action = `sanity:${source}`
    const message = `Error fetching ${source} from Sanity`

    if (e instanceof ZodError) {
      logError(e, 'Sanity item is invalid.', action)
    } else if (isTransportError(e)) {
      if (navigator.onLine === false) {
        console.warn('Offline while fetching from Sanity: ', e)
        return
      }
      logError(rethrownFromCaller(e), message, action)
    } else {
      logError(e, message, action)
    }
  }

  return {
    perspective,
    dataStore,
    handleDataStoreError,
    disableValidations,
    dataStoreSWRVKey
  }
}
